/**
 * Pure helpers for the Retransmisje tab.
 *
 * Two honesty rules are encoded here rather than left to the presenter:
 *
 * 1. The Redis rollups are counters and last-value gauges (design §3.2), so a quantile cannot be
 *    rebuilt from them. Every quantile on this tab comes from Prometheus through `GET /dod/promql`
 *    and is hidden when Prometheus is absent.
 * 2. No endpoint reports "attempts until success". The attempt distribution below is derived from
 *    orders that have NOT succeeded -- live queue rows and dead-letter entries -- which is a biased
 *    sample, and the panel says so.
 */

export type RetriesSeriesPoint = [number, number | null];

export type RetriesSeries = {
  key: string;
  labels: Record<string, string>;
  points: RetriesSeriesPoint[];
};

/** Counter rollups gap-fill with 0, but a gauge is null before its first observation. */
export const seriesTotal = (series: RetriesSeries): number =>
  series.points.reduce((sum, [, value]) => sum + (value ?? 0), 0);

export const seriesPointsAsNumbers = (series: RetriesSeries): Array<[number, number]> =>
  series.points.map(([timestamp, value]) => [timestamp, value ?? 0]);

/** Turns grouped rollup series into the horizontal bars of a BarPanel, largest first. */
export const seriesToBars = (
  series: RetriesSeries[],
  labelFor: (key: string, labels: Record<string, string>) => string,
): Array<{ id: string; label: string; value: number }> =>
  series
    .map((one) => ({ id: one.key, label: labelFor(one.key, one.labels), value: seriesTotal(one) }))
    .filter((bar) => bar.value > 0)
    .sort((left, right) => right.value - left.value);

export type AttemptBucket = {
  attempt: number;
  /** Orders still sitting in a queue whose highest attempt count is this number. */
  queued: number;
  /** Dead-lettered orders that used this many attempts. */
  dead: number;
  total: number;
};

export type AttemptHistogramInput = {
  /** `retryCount` of every dead-letter entry on the current page. */
  dlqRetryCounts: number[];
  /** `retryCountMax` of every live queue row. */
  queueRetryMaxes: number[];
};

/**
 * Attempt distribution over orders that have not succeeded.
 *
 * This is deliberately NOT called "attempts until success": a delivered order leaves no retry
 * counter behind anywhere the monitor can read, so the sample is censored. Buckets with nothing in
 * them are dropped rather than drawn as zero-height bars.
 */
export const deriveAttemptHistogram = ({ dlqRetryCounts, queueRetryMaxes }: AttemptHistogramInput): AttemptBucket[] => {
  const buckets = new Map<number, AttemptBucket>();

  const bucketFor = (attempt: number): AttemptBucket => {
    const existing = buckets.get(attempt);

    if (existing) {
      return existing;
    }

    const created: AttemptBucket = { attempt, queued: 0, dead: 0, total: 0 };
    buckets.set(attempt, created);

    return created;
  };

  dlqRetryCounts
    .filter((attempt) => Number.isFinite(attempt) && attempt > 0)
    .forEach((attempt) => {
      const bucket = bucketFor(attempt);
      bucket.dead += 1;
      bucket.total += 1;
    });

  queueRetryMaxes
    .filter((attempt) => Number.isFinite(attempt) && attempt > 0)
    .forEach((attempt) => {
      const bucket = bucketFor(attempt);
      bucket.queued += 1;
      bucket.total += 1;
    });

  return Array.from(buckets.values()).sort((left, right) => left.attempt - right.attempt);
};

export type PromqlQuantile = 0.5 | 0.95 | 0.99;

/**
 * `histogram_quantile` over the delivery-duration histogram.
 *
 * The rate window is tied to the step so a wide range does not average a spike away entirely, and
 * the metric name is one of the families the backend's allow-list accepts (`dod_*`).
 */
export const deliveryQuantileQuery = (quantile: PromqlQuantile, stepSeconds: number): string =>
  `histogram_quantile(${quantile}, sum(rate(dod_order_delivery_duration_seconds_bucket[${Math.max(
    stepSeconds,
    60,
  )}s])) by (le))`;

/** Send-duration p95 split per command, which is what the "send p95 per command" panel draws. */
export const sendP95PerCommandQuery = (stepSeconds: number): string =>
  `histogram_quantile(0.95, sum(rate(dod_order_send_duration_seconds_bucket[${Math.max(
    stepSeconds,
    60,
  )}s])) by (le, command))`;

export type PromqlMatrix = {
  status: string;
  data?: {
    resultType: string;
    result: Array<{ metric: Record<string, string>; values: Array<[number, string]> }>;
  };
};

/**
 * Prometheus `query_range` matrix to the series shape the chart panels take.
 *
 * Prometheus timestamps are seconds and its values are strings; both are converted here so no
 * presenter has to know that. A non-numeric value (`NaN`, which Prometheus emits for an empty
 * quantile) becomes null, so the line breaks instead of dropping to zero.
 */
export const promqlToSeries = (
  response: PromqlMatrix | undefined,
  keyFor: (metric: Record<string, string>) => string,
): RetriesSeries[] => {
  if (!response || response.status !== 'success' || !response.data) {
    return [];
  }

  return response.data.result.map((result) => ({
    key: keyFor(result.metric),
    labels: result.metric,
    points: result.values.map(([seconds, value]): RetriesSeriesPoint => {
      const parsed = Number(value);

      return [seconds * 1000, Number.isFinite(parsed) ? parsed : null];
    }),
  }));
};

/** Seconds to a short human duration: 0.412 s -> "412 ms", 3.5 -> "3.5 s". */
export const formatSeconds = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return '—';
  }

  if (seconds < 1) {
    return `${Math.round(seconds * 1000)} ms`;
  }

  return `${seconds.toFixed(1)} s`;
};

/** Last non-null value of a series, which is what a quantile tile prints. */
export const lastValue = (series: RetriesSeries | undefined): number | null => {
  if (!series) {
    return null;
  }

  for (let index = series.points.length - 1; index >= 0; index -= 1) {
    const value = series.points[index][1];

    if (value !== null) {
      return value;
    }
  }

  return null;
};
